import { useState, useRef, useCallback, type DragEvent, type ChangeEvent } from "react";
import { Microphone, FileText } from "@phosphor-icons/react";
import { type UploadProgress, formatFileSize } from "@/lib/storage/upload";
import { Eyebrow } from "@/components/ui";

interface FileUploaderProps {
  accept: string;
  multiple?: boolean;
  maxFiles?: number;
  fileType: "audio" | "text";
  onFilesSelected: (files: File[]) => void;
  uploadProgress?: UploadProgress[];
  label: string;
  description: string;
}

/**
 * FileUploader — drop-zone with mono labels.
 * Walnut tint on drag-over, walnut hairline progress bar.
 */
export function FileUploader({
  accept, multiple = true, maxFiles = 20, fileType,
  onFilesSelected, uploadProgress = [], label, description,
}: FileUploaderProps) {
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDragOver  = useCallback((e: DragEvent) => { e.preventDefault(); setIsDragging(true); }, []);
  const handleDragLeave = useCallback((e: DragEvent) => { e.preventDefault(); setIsDragging(false); }, []);
  const handleDrop = useCallback((e: DragEvent) => {
    e.preventDefault(); setIsDragging(false);
    const f = Array.from(e.dataTransfer.files).slice(0, maxFiles);
    if (f.length > 0) onFilesSelected(f);
  }, [maxFiles, onFilesSelected]);
  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const f = Array.from(e.target.files || []).slice(0, maxFiles);
    if (f.length > 0) onFilesSelected(f);
    e.target.value = "";
  }, [maxFiles, onFilesSelected]);

  return (
    <div className="space-y-4">
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className={
          "flex cursor-pointer flex-col items-center justify-center rounded-as-3 border border-dashed px-5 py-8 transition-colors duration-as-1 " +
          (isDragging
            ? "border-walnut bg-walnut-tint/60"
            : "border-rule-strong bg-paper-raised hover:border-walnut hover:bg-walnut-tint/30")
        }
      >
        <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-as-pill bg-walnut-tint">
          {fileType === "audio"
            ? <Microphone size={20} weight="duotone" className="text-walnut-deep" />
            : <FileText size={20} weight="duotone" className="text-walnut-deep" />}
        </div>
        <p className="text-center font-display text-t-16 font-semibold leading-as-snug text-ink-1">{label}</p>
        <p className="mt-2 text-center font-mono text-[10px] uppercase tracking-[0.14em] text-ink-3">{description}</p>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleChange}
          className="hidden"
        />
      </div>

      {uploadProgress.length > 0 && (
        <div className="space-y-2">
          {uploadProgress.map((progress, idx) => {
            const barColor =
              progress.status === "error"    ? "bg-flag" :
              progress.status === "complete" ? "bg-verify" :
              "bg-walnut";
            return (
              <div key={idx} className="rounded-as-2 bg-paper-raised p-3 shadow-as-1">
                <div className="mb-2 flex items-center justify-between gap-3">
                  <span className="font-display font-semibold text-t-14 text-ink-1 truncate max-w-[70%]">
                    {progress.file_name}
                  </span>
                  <Eyebrow>
                    {progress.status === "complete" ? formatFileSize(progress.total_bytes)
                      : progress.status === "error" ? "Failed"
                      : `${progress.percent}%`}
                  </Eyebrow>
                </div>
                <div className="h-[3px] overflow-hidden rounded-full bg-rule">
                  <div
                    className={`h-full rounded-full transition-[width] duration-as-2 ${barColor}`}
                    style={{ width: `${progress.percent}%` }}
                  />
                </div>
                {progress.error && (
                  <p className="mt-2 font-mono text-[11px] tracking-as-eyebrow uppercase text-flag">
                    {progress.error}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
